import React, { lazy, Suspense, useState, memo } from 'react';
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import Loader from "../components/Layout/Loader";
import Header from '../components/Layout/Header';

// Lazy load non-critical components
const Hero = lazy(() => import('../components/Route/Hero/Hero'));
const Categories = lazy(() => import('../components/Route/Categories/Categories'));

const HomePage = () => {
  // Use the selector directly within the component
  const isLoading = useSelector((state) => state.products?.isLoading || false);
  const [showPopup, setShowPopup] = useState(true);

  // States for deferred components
  const [BestDealsComp, setBestDealsComp] = useState(null);
  const [FeaturedProductComp, setFeaturedProductComp] = useState(null);
  const [SponsoredComp, setSponsoredComp] = useState(null);
  const [FooterComp, setFooterComp] = useState(null);

  const closePopup = () => {
    setShowPopup(false);
  };

  // Defer loading of non-critical components after initial render
  React.useEffect(() => {
    // Use requestIdleCallback or setTimeout to defer loading
    const timeoutId = setTimeout(() => {
      const loadDeferred = async () => {
        const [BestDeals, FeaturedProduct, Sponsored, Footer] = await Promise.all([
          import('../components/Route/BestDeals/BestDeals'),
          import('../components/Route/FeaturedProduct/FeaturedProduct'),
          import('../components/Route/Sponsored'),
          import('../components/Layout/Footer'),
        ]);
        setBestDealsComp(() => BestDeals.default);
        setFeaturedProductComp(() => FeaturedProduct.default);
        setSponsoredComp(() => Sponsored.default);
        setFooterComp(() => Footer.default);
      };
      loadDeferred();
    }, 100); // Small delay to prioritize above-the-fold content
    
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div>
      {showPopup && (
        <div style={popupStyles}>
          <button onClick={closePopup} style={closeButtonStyles}>
            ×
          </button>
          <p style={popupTextStyles}>
            Please search your product in the search bar. If you can't find the product you're looking for, our sourcing team is here to help! We can source the product and meet your exact requirements. Simply send your detailed requirements to us at 
            <a href="mailto:sales@hawler.co.in" style={linkStyles}> sales@hawler.co.in</a> or WhatsApp us at 
            <a href="https://wa.me/918072368475" style={linkStyles}>+91 8072368475</a>.
            <br /><br />
            Cash on Delivery is available only within Coimbatore. 
          </p>
        </div>
      )}
      <Helmet>
        <title>Hawler - Your Trusted Multiproduct Industrial Marketplace in India</title>
        <meta name="description" content="Discover a wide range of products from multiple vendors at Hawler. Serving Coimbatore and throughout India with quality and affordability." />
        <meta name="keywords" content="Bolt, Hawler, Coimbatore, South India, nut, washer, grinding wheel, welding coil, welding, welding rod, screw, screwdriver, power tool, fasteners, consumables, packing items, common electrical spares, measuring tools, vernier, safety and security, office stationary products, cutting tools and machines, chemicals and lubricants, oil " />
        <meta property="og:title" content="Hawler - Your Trusted Multiproduct Industrial Marketplace in India" />
        <meta property="og:description" content="Discover a wide range of products from multiple vendors at Hawler. Serving Coimbatore and throughout India with quality and affordability." />
        <meta property="og:image" content="https://res.cloudinary.com/dj1w38zh5/image/upload/v1727440595/avatars/logo_vpr0jm.png" />
        <meta property="og:url" content="https://www.hawler.co.in/" />
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Store",
            "name": "Hawler",
            "url": "https://www.hawler.co.in/",
            "logo": "https://res.cloudinary.com/dj1w38zh5/image/upload/v1727440595/avatars/logo_vpr0jm.png",
            "description": "Discover a wide range of products from multiple vendors at Hawler. Serving Coimbatore and throughout India with quality and affordability.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "95, Shree balaji Nagar, Rakkipalayam, NSN palayam, coimbatore - 641031",
              "addressLocality": "Coimbatore",
              "addressRegion": "Tamil Nadu",
              "postalCode": "641031",
              "addressCountry": "IN"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "11.0168",
              "longitude": "76.9558"
            },
            "openingHours": "Mo-Sa 09:00-18:00",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8072368475",
              "contactType": "Customer Service"
            }
          })}
        </script>
      </Helmet>
      <Header activeHeading={1} />

      {isLoading ? (
        <div className="flex items-center justify-center h-screen bg-gray-100">
          <Loader />
        </div>
      ) : (
        <>
          <Suspense fallback={<Loader />}>
            <Hero />
          </Suspense>
          <Suspense fallback={<Loader />}>
            <Categories />
          </Suspense>
          {/* Defer loading of below-the-fold components */}
          {BestDealsComp && <BestDealsComp />}
          {FeaturedProductComp && <FeaturedProductComp />}
          {SponsoredComp && <SponsoredComp />}
          {FooterComp && <FooterComp />}
        </>
      )}
    </div>
  );
};

// Styles extracted for readability
const popupStyles = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '400px',
  padding: '20px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  zIndex: 1000,
  textAlign: 'center',
};

const closeButtonStyles = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  background: 'none',
  border: 'none',
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
};

const popupTextStyles = {
  fontSize: '16px',
  marginBottom: '16px',
};

const linkStyles = {
  color: 'blue',
  textDecoration: 'underline',
};

// Wrap HomePage with React.memo to prevent unnecessary re-renders
export default memo(HomePage);
